import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { ContextService } from '../context/context.service';
import { AuthService } from './auth.service';
import { loginUrlTree, safeReturnUrl } from './return-url';

/** Signed-in users only; others go to the login page and come back to the same URL afterwards. */
export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  return auth.isAuthenticated() || loginUrlTree(inject(Router), state.url);
};

/** The login page is for signed-out users; signed-in users go to their return URL or home. */
export const loginGuard: CanActivateFn = (route) => {
  const auth = inject(AuthService);
  if (!auth.isAuthenticated()) return true;

  const router = inject(Router);
  return router.parseUrl(safeReturnUrl(route.queryParamMap.get('returnUrl')) ?? '/');
};

/** The admin area: administrators and app admins (checked against /api/me/context); others go home. */
export const adminGuard: CanActivateFn = (_route, state) =>
  whenContextLoaded((context) => context.canAdminister(), state.url);

/** Pages for administrators only (e.g. Organisation); app admins go to People & access instead. */
export const platformAdminGuard: CanActivateFn = (_route, state) =>
  whenContextLoaded((context) => context.isAdmin(), state.url, inject(Router).parseUrl('/admin/people'));

/**
 * Users who may open the application only. The code comes from the route data (`appCode`) or the `:appCode`
 * parameter. Others see the "no access" page for that application.
 */
export const appAccessGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state) => {
  const code = (route.data['appCode'] as string | undefined) ?? route.paramMap.get('appCode') ?? '';
  const router = inject(Router);
  return whenContextLoaded(
    (context) => context.canOpen(code),
    state.url,
    router.createUrlTree(['/no-access'], { queryParams: { app: code } })
  );
};

/** Waits for the user's context, then lets them through when `allowed` says so. */
function whenContextLoaded(
  allowed: (context: ContextService) => boolean,
  returnUrl: string,
  otherwise?: UrlTree
): Observable<boolean | UrlTree> | UrlTree {
  const auth = inject(AuthService);
  const context = inject(ContextService);
  const router = inject(Router);
  if (!auth.isAuthenticated()) return loginUrlTree(router, returnUrl);

  const fallback = otherwise ?? router.parseUrl('/');
  return context.load().pipe(
    map(() => allowed(context) || fallback),
    // The context could not be loaded (e.g. the API is down): stay on a page that explains it.
    catchError(() => of(router.createUrlTree(['/no-access'], { queryParams: { reason: 'unavailable' } })))
  );
}
